import { type $dfsIterator as $lexicalDfsIterator, $getDepth, type DFSNode } from '@lexical/utils';
import type { LexicalNode } from 'lexical';
import { $getRoot, $isElementNode } from 'lexical';

export type DFSIterator = ReturnType<typeof $lexicalDfsIterator>;

/**
 * An iterator which will traverse the tree in exactly the mirrored order of $dfsIterator.
 * This will be true:
 *
 * Array.from($dfsIterator()).reverse() === Array.from($mirroredDfsIterator())
 *
 * Tree traversal is done on the fly as new values are requested with O(1) memory.
 *
 * Note: lexical has $reverseDfsIterator, but that is actually just a right-to-left depth-first-search,
 * which is not the same:
 * - $mirroredDfsIterator() will start at a leaf node and work towards the left, ending at the root node.
 * - $reverseDfsIterator() will start at a parent and traverse down and towards the left, ending at the leftmost leaf node.
 *
 * @param startNode - The node to start the search. If omitted, it will start at the last leaf node in the tree.
 * @param endNode - The node to end the search. If omitted, it will work backwards all the way to the root node
 * @returns An iterator, each yielded value is a DFSNode. It will always return at least 1 node (the start node).
 */
export function $mirroredDfsIterator(startNode?: LexicalNode, endNode?: LexicalNode): DFSIterator {
    const start = (startNode || $getRoot().getLastDescendant() || $getRoot()).getLatest();
    const startDepth = $getDepth(start);
    const end = endNode || $getRoot();
    let node: null | LexicalNode = start;
    let depth = startDepth;
    let isFirstNext = true;

    const iterator: DFSIterator = {
        next(): IteratorResult<DFSNode, void> {
            if (node === null) {
                return iteratorDone;
            }
            if (isFirstNext) {
                isFirstNext = false;
                return iteratorNotDone({ depth, node });
            }
            if (node === end) {
                return iteratorDone;
            }
            if (node.getPreviousSibling()) {
                let depthDiff;
                [node, depthDiff] = $getPreviousSiblingsLastDescendantOrPreviousSibling(node) || [
                    null,
                    0,
                ];
                depth += depthDiff;
            } else {
                node = node.getParent();
                depth--;
            }
            if (node === null) {
                return iteratorDone;
            }
            return iteratorNotDone({ depth, node });
        },
        [Symbol.iterator](): DFSIterator {
            return iterator;
        },
    };
    return iterator;
}

/**
 * Returns the previous sibling's last descendant (when it exists) or the previous sibling.
 * R -> P -> T1
 *        -> T2
 *   -> P2
 * returns T1 for node T2, T2 for node P2, and null for node T1 or P.
 * @param node LexicalNode.
 * @returns an array (tuple) coontaining the found Lexical node and the depth difference, or null, if this node doesn't exist.
 */
function $getPreviousSiblingsLastDescendantOrPreviousSibling(
    node: LexicalNode,
): null | [LexicalNode, number] {
    let _node: LexicalNode | null = node.getPreviousSibling();
    let depthDiff = 0;
    while ($isElementNode(_node) && _node.getChildrenSize() > 0) {
        _node = _node.getLastChild();
        depthDiff++;
    }
    if (_node === null) {
        return null;
    }
    return [_node, depthDiff];
}

const iteratorDone: Readonly<{ done: true; value: void }> = {
    done: true,
    value: undefined,
};

const iteratorNotDone: <T>(value: T) => Readonly<{ done: false; value: T }> = <T>(value: T) => ({
    done: false,
    value,
});

export const emptyDFSIterator: DFSIterator = {
    next(): IteratorResult<DFSNode, void> {
        return {
            done: true,
            value: undefined,
        };
    },
    [Symbol.iterator]() {
        return emptyDFSIterator;
    },
};
